// Kontextmenü für Termine (Rechtsklick) — extrahiert aus AppointmentsPage.js.
// 06/2026: Einträge & Reihenfolge sind pro Mandant konfigurierbar (menuConfig,
// geladen aus /api/settings/calendar-menu). Fest sind nur Kundenprofil,
// Verschieben und Check-in.
import React from 'react';
import { AlertCircle, Calendar as CalendarIcon, CheckCircle, Clock, Edit, FileText, History, ListChecks, MessageCircle, Repeat, Shirt, ShoppingBag, Smartphone, StickyNote, UserCircle, X } from 'lucide-react';

const DEFAULT_MENU_ORDER = [
  'edit', 'profile', 'send_app_link', 'add_note', 'whatsapp_reschedule', 'anamnese',
  'suit_size', 'custom_fields', 'checkin', 'sell_product', 'move', 'move_participant',
  'manage_series', 'reset_billing', 'remove_delete', 'lock_slot', 'history',
];
const FIXED_KEYS = new Set(['profile', 'move', 'checkin']);

export const AppointmentContextMenu = ({
  contextMenu, onClose, appointment, services, trainers, anamneseStatus,
  onEdit, onViewAnamnese, onCheckIn, onMove, onRemoveParticipant,
  onAvailableSlotClick, onOpenDeleteOptions, onManageSeries, onShowHistory, onResetBilling, onMoveParticipant, onSellProduct, onEditSuitSize,
  onGoToProfile, onSendAppLink, onWhatsAppReschedule, onAddNote, onEditCustomFields, menuConfig,
}) => {
  const [removeChoice, setRemoveChoice] = React.useState(false);
  const menuRef = React.useRef(null);
  const [pos, setPos] = React.useState(null);
  React.useLayoutEffect(() => {
    if (!contextMenu) return;
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pad = 8;
    let top = contextMenu.y;
    let left = contextMenu.x;
    if (top + rect.height > window.innerHeight - pad) top = Math.max(pad, window.innerHeight - rect.height - pad);
    if (left + rect.width > window.innerWidth - pad) left = Math.max(pad, window.innerWidth - rect.width - pad);
    setPos({ top, left });
  }, [contextMenu?.x, contextMenu?.y]);
  if (!contextMenu) return null;

  // Reihenfolge + Sichtbarkeit aus Mandanten-Konfiguration ableiten. Neue
  // Standard-Einträge (die es beim Speichern der Config noch nicht gab) werden
  // an ihrer Default-Position eingefügt statt ans Ende gehängt.
  let orderedKeys;
  if (Array.isArray(menuConfig) && menuConfig.length) {
    orderedKeys = menuConfig.map((e) => e.key);
    DEFAULT_MENU_ORDER.forEach((key, i) => {
      if (orderedKeys.includes(key)) return;
      let insertAt = orderedKeys.length;
      for (let j = i - 1; j >= 0; j--) {
        const idx = orderedKeys.indexOf(DEFAULT_MENU_ORDER[j]);
        if (idx >= 0) { insertAt = idx + 1; break; }
      }
      orderedKeys.splice(insertAt, 0, key);
    });
  } else {
    orderedKeys = DEFAULT_MENU_ORDER;
  }
  const enabledByKey = {};
  if (Array.isArray(menuConfig)) {
    menuConfig.forEach((e) => { enabledByKey[e.key] = e.enabled !== false; });
  }
  const isEnabled = (key) => FIXED_KEYS.has(key) || (key in enabledByKey ? enabledByKey[key] : true);

  // 08/2026: Teilnehmer-genaue Abrechnungs-Anzeige (nach Einzel-Reset zeigte
  // das Menü fälschlich "nicht abgerechnet", obwohl z.B. Felix noch billed ist)
  const _pbIds = (() => {
    if (!appointment) return [];
    const ids = [...(appointment.customer_ids || [])];
    if (appointment.customer_id && !ids.includes(appointment.customer_id)) ids.unshift(appointment.customer_id);
    return ids;
  })();
  const _pb = appointment?.participants_billing || {};
  const _billedCount = _pbIds.filter((id) => ['billed', 'paid'].includes((_pb[id] || {}).status)).length;
  const _fullyBilled = !!appointment && (appointment.billing_status === 'billed' || appointment.billing_status === 'paid');
  const _partiallyBilled = !_fullyBilled && _billedCount > 0;

  // Jeder Menüeintrag als eigenständiger Knoten; null = Bedingungen nicht erfüllt.
  const nodes = {
    edit: (
      <button
        key="edit"
        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
        onClick={onEdit}
        data-testid="ctx-menu-edit"
      >
        <Edit size={16} />
        Bearbeiten
      </button>
    ),
    profile: (contextMenu.customerId && onGoToProfile) ? (
      <button
        key="profile"
        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
        onClick={() => onGoToProfile(contextMenu.customerId)}
        data-testid="ctx-menu-goto-profile"
      >
        <UserCircle size={16} />
        Zum Profil
      </button>
    ) : null,
    send_app_link: (contextMenu.customerId && onSendAppLink) ? (
      <button
        key="send_app_link"
        className="w-full px-4 py-2 text-left hover:bg-emerald-50 text-emerald-700 flex items-center gap-2"
        onClick={() => onSendAppLink(contextMenu.customerId)}
        data-testid="ctx-menu-send-app-link"
      >
        <Smartphone size={16} />
        App-Link senden
      </button>
    ) : null,
    add_note: (contextMenu.customerId && onAddNote) ? (
      <button
        key="add_note"
        className="w-full px-4 py-2 text-left hover:bg-amber-50 text-amber-700 flex items-center gap-2"
        onClick={() => onAddNote(contextMenu.customerId)}
        data-testid="ctx-menu-add-note"
      >
        <StickyNote size={16} />
        Notiz hinzufügen
      </button>
    ) : null,
    whatsapp_reschedule: (contextMenu.customerId && onWhatsAppReschedule && appointment &&
      appointment.billing_status !== 'billed' && appointment.billing_status !== 'paid') ? (
      <button
        key="whatsapp_reschedule"
        className="w-full px-4 py-2 text-left hover:bg-emerald-50 text-emerald-700 flex items-center gap-2"
        onClick={() => { onWhatsAppReschedule(appointment.id); onClose(); }}
        data-testid="ctx-menu-wa-reschedule"
      >
        <MessageCircle size={16} />
        Per WhatsApp verschieben
      </button>
    ) : null,
    anamnese: contextMenu.customerId ? (
      <button
        key="anamnese"
        className={`w-full px-4 py-2 text-left flex items-center gap-2 ${
          anamneseStatus[contextMenu.customerId]
            ? 'hover:bg-blue-100 text-blue-700'
            : 'hover:bg-red-100 text-red-600'
        }`}
        onClick={() => onViewAnamnese(contextMenu.customerId)}
        data-testid="ctx-menu-anamnese"
      >
        {anamneseStatus[contextMenu.customerId] ? (
          <><FileText size={16} />Anamnese</>
        ) : (
          <><AlertCircle size={16} />Anamnese (fehlt!)</>
        )}
      </button>
    ) : null,
    suit_size: (contextMenu.customerId && onEditSuitSize) ? (
      <button
        key="suit_size"
        className="w-full px-4 py-2 text-left hover:bg-purple-50 text-purple-700 flex items-center gap-2"
        onClick={() => {
          const _idx = (appointment?.customer_ids || []).indexOf(contextMenu.customerId);
          const _cur = _idx >= 0 ? ((appointment?.customer_suit_sizes || [])[_idx] ?? '') : '';
          const _nm = _idx >= 0 ? ((appointment?.customer_names || [])[_idx] || '') : '';
          const _val = window.prompt(
            `EMS-Anzugsgrösse${_nm ? ' für ' + _nm.split(' ')[0] : ''} (z.B. 4, 2.5, 4NEU):`,
            _cur === null || _cur === undefined ? '' : String(_cur)
          );
          if (_val !== null) {
            onEditSuitSize(contextMenu.customerId, _val.trim());
          }
          onClose();
        }}
        data-testid="ctx-menu-suit-size"
      >
        <Shirt size={16} />
        EMS-Anzugsgrösse
      </button>
    ) : null,
    custom_fields: (contextMenu.customerId && onEditCustomFields) ? (
      <button
        key="custom_fields"
        className="w-full px-4 py-2 text-left hover:bg-indigo-50 text-indigo-700 flex items-center gap-2"
        onClick={() => { onEditCustomFields(contextMenu.customerId); onClose(); }}
        data-testid="ctx-menu-custom-fields"
      >
        <ListChecks size={16} />
        Eigene Felder
      </button>
    ) : null,
    checkin: (
      <React.Fragment key="checkin">
        {appointment &&
         !appointment.checked_in &&
         appointment.billing_status !== 'billed' &&
         appointment.billing_status !== 'paid' && (
          <button
            className="w-full px-4 py-2 text-left hover:bg-green-100 flex items-center gap-2 text-green-700"
            onClick={onCheckIn}
            data-testid="ctx-menu-checkin"
          >
            <CheckCircle size={16} />
            Check-In
          </button>
        )}
        {appointment && (appointment.checked_in || _fullyBilled) && (
          <div className="w-full px-4 py-2 flex items-center gap-2 text-green-600 bg-green-50">
            <CheckCircle size={16} />
            <span className="font-medium">✓ Bereits abgerechnet</span>
          </div>
        )}
        {appointment && !appointment.checked_in && _partiallyBilled && (
          <div className="w-full px-4 py-2 flex items-center gap-2 text-amber-700 bg-amber-50" data-testid="ctx-partial-billed">
            <CheckCircle size={16} />
            <span className="font-medium">◐ Teilweise abgerechnet ({_billedCount}/{_pbIds.length})</span>
          </div>
        )}
      </React.Fragment>
    ),
    sell_product: (appointment && onSellProduct && _pbIds.length > 0) ? (
      <button
        key="sell_product"
        className="w-full px-4 py-2 text-left hover:bg-emerald-50 text-emerald-700 flex items-center gap-2"
        onClick={() => {
          const cid = (contextMenu.customerId && _pbIds.includes(contextMenu.customerId))
            ? contextMenu.customerId : _pbIds[0];
          const nm = (appointment?.customer_names || [])[_pbIds.indexOf(cid)] || '';
          onSellProduct(cid, nm);
        }}
        data-testid="ctx-menu-sell-product"
      >
        <ShoppingBag size={16} />
        Produkt verkaufen{(() => {
          const cid = (contextMenu.customerId && _pbIds.includes(contextMenu.customerId))
            ? contextMenu.customerId : _pbIds[0];
          const nm = (appointment?.customer_names || [])[_pbIds.indexOf(cid)];
          return nm ? ` (${nm.split(' ')[0]})` : '';
        })()}
      </button>
    ) : null,
    move: (
      <button
        key="move"
        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
        onClick={onMove}
        data-testid="ctx-menu-move"
      >
        <CalendarIcon size={16} />
        {(_pbIds.length > 1) ? 'Ganzen Termin verschieben' : 'Verschieben'}
      </button>
    ),
    move_participant: (onMoveParticipant && contextMenu.customerId && _pbIds.length > 1) ? (
      <button
        key="move_participant"
        className="w-full px-4 py-2 text-left hover:bg-teal-50 text-teal-700 flex items-center gap-2"
        onClick={() => {
          const _idx = _pbIds.indexOf(contextMenu.customerId);
          const _nm = _idx >= 0 ? (appointment?.customer_names || [])[_idx] : null;
          onMoveParticipant(contextMenu.customerId, _nm);
        }}
        data-testid="ctx-menu-move-participant"
      >
        <CalendarIcon size={16} />
        Nur {(() => {
          const _idx = _pbIds.indexOf(contextMenu.customerId);
          const full = _idx >= 0 ? (appointment?.customer_names || [])[_idx] : null;
          return full ? full.split(' ')[0] : 'Teilnehmer';
        })()} verschieben
      </button>
    ) : null,
    manage_series: (appointment?.is_series && appointment?.series_id && onManageSeries) ? (
      <button
        key="manage_series"
        className="w-full px-4 py-2 text-left hover:bg-indigo-50 text-indigo-700 flex items-center gap-2"
        onClick={() => { onManageSeries(appointment); onClose(); }}
        data-testid="ctx-menu-manage-series"
      >
        <Repeat size={16} />
        Serie verwalten
      </button>
    ) : null,
    reset_billing: (appointment && onResetBilling &&
      (_fullyBilled || _partiallyBilled || appointment.invoice_id || appointment.billing_reset_undo)) ? (
      <button
        key="reset_billing"
        className="w-full px-4 py-2 text-left hover:bg-amber-50 text-amber-700 flex items-center gap-2"
        onClick={onResetBilling}
        data-testid="ctx-menu-reset-billing"
      >
        <Repeat size={16} />
        Abrechnung zurücksetzen
      </button>
    ) : null,
    remove_delete: (() => {
      const service = services.find(s => s.id === appointment?.service_id);
      const numParticipants = appointment?.customer_ids?.length || 1;
      const hasMultiple = contextMenu.customerId && numParticipants > 1;

      if (hasMultiple) {
        if (appointment?.is_series) {
          if (!removeChoice) {
            return (
              <button
                key="remove_delete"
                className="w-full px-4 py-2 text-left hover:bg-red-100 text-red-600 flex items-center gap-2"
                onClick={() => setRemoveChoice(true)}
                data-testid="ctx-menu-remove-participant"
              >
                <X size={16} />
                Teilnehmer entfernen
              </button>
            );
          }
          return (
            <div key="remove_delete" className="border-t border-b bg-red-50/40">
              <div className="px-4 pt-2 pb-1 text-xs text-gray-500">Teilnehmer entfernen aus …</div>
              <button
                className="w-full px-4 py-2 text-left hover:bg-red-100 text-red-600 flex items-center gap-2"
                onClick={() => { onRemoveParticipant(appointment.id, contextMenu.customerId, 'this'); onClose(); }}
                data-testid="ctx-menu-remove-this"
              >
                <X size={16} />
                Nur diesem Termin
              </button>
              <button
                className="w-full px-4 py-2 text-left hover:bg-red-100 text-red-600 flex items-center gap-2"
                onClick={() => { onRemoveParticipant(appointment.id, contextMenu.customerId, 'future'); onClose(); }}
                data-testid="ctx-menu-remove-series"
              >
                <X size={16} />
                Allen Serienterminen
              </button>
            </div>
          );
        }
        return (
          <button
            key="remove_delete"
            className="w-full px-4 py-2 text-left hover:bg-red-100 text-red-600 flex items-center gap-2"
            onClick={() => {
              if (window.confirm(`Diesen Teilnehmer vom Termin entfernen?\n\nDie anderen ${numParticipants - 1} Teilnehmer bleiben im Termin.`)) {
                onRemoveParticipant(appointment.id, contextMenu.customerId, 'this');
                onClose();
              }
            }}
            data-testid="ctx-menu-remove-participant"
          >
            <X size={16} />
            Teilnehmer entfernen
          </button>
        );
      }

      const isGroupTraining = service && service.max_participants > 1;
      if (isGroupTraining && numParticipants > 1) {
        return (
          <div key="remove_delete" className="px-4 py-2 text-xs text-gray-500 italic border-t">
            ℹ️ Bitte einzelne Teilnehmer entfernen. Termin wird automatisch gelöscht wenn letzter Teilnehmer entfernt wird.
          </div>
        );
      }
      return (
        <button
          key="remove_delete"
          className="w-full px-4 py-2 text-left hover:bg-red-100 text-red-600 flex items-center gap-2"
          onClick={() => { onClose(); onOpenDeleteOptions(); }}
          data-testid="ctx-menu-delete"
        >
          <X size={16} />
          Löschen
        </button>
      );
    })(),
    lock_slot: appointment ? (
      <button
        key="lock_slot"
        className="w-full px-4 py-2 text-left hover:bg-yellow-100 flex items-center gap-2 text-yellow-700 border-t"
        onClick={() => {
          const trainer = trainers.find(t => t.trainer.id === appointment.trainer_id);
          const aptStart = new Date(appointment.start_time);
          const timeSlot = `${aptStart.getHours().toString().padStart(2, '0')}:${aptStart.getMinutes().toString().padStart(2, '0')}`;
          if (trainer) {
            onAvailableSlotClick(trainer, timeSlot);
          }
          onClose();
        }}
        data-testid="ctx-menu-lock"
      >
        <Clock size={16} />
        Slot sperren
      </button>
    ) : null,
    history: (appointment && onShowHistory) ? (
      <button
        key="history"
        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 border-t"
        onClick={onShowHistory}
        data-testid="ctx-menu-history"
      >
        <History size={16} />
        Historie
      </button>
    ) : null,
  };

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        ref={menuRef}
        className="fixed z-50 bg-white rounded-lg shadow-lg border py-1 min-w-[150px] overflow-y-auto"
        style={{ top: (pos?.top ?? contextMenu.y), left: (pos?.left ?? contextMenu.x), maxHeight: 'calc(100vh - 16px)' }}
        data-testid="appointment-context-menu"
      >
        {orderedKeys.map((key) => (isEnabled(key) ? nodes[key] : null))}
      </div>
    </>
  );
};
